import React, { useState } from "react";
import {
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Grid,
  Pagination,
  AppBar,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import toast from "react-hot-toast";
import {
  Visibility,
  PlayArrow,
  Delete,
  Memory,
  Extension,
  Speed,
  Favorite,
  SportsEsports,
  Cancel,
  HourglassEmpty,
  Public,
} from "@mui/icons-material";
import RunAndTest from "components/play-ground/Run";
import {
  useGetAllPublicLevelsQuery,
  useGetPendingPublicLevelsQuery,
  useGetRejectedPublicLevelsQuery,
  useApprovePendingLevelMutation,
  useRejectPendingLevelMutation,
} from "lib/store/service/public";

function PublicLevelsManagement() {
  const [tabValue, setTabValue] = useState(0);

  // --- 1. Published Levels Service ---
  const [pubPage, setPubPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [rejectedPage, setRejectedPage] = useState(1);

  const { data: pubData, isLoading: isLoadingPub, isFetching: isFetchingPub, refetch: refetchPub } = useGetAllPublicLevelsQuery({ page: pubPage, limit: 10 });

  // --- 2. Pending Approvals Service ---
  const { data: pendingData, isLoading: isLoadingPending, isFetching: isFetchingPending, refetch: refetchPending } = useGetPendingPublicLevelsQuery({ page: pendingPage, limit: 10 });
  
  // --- 3. Rejected Levels Service ---
  const { data: rejectedData, isLoading: isLoadingRejected, isFetching: isFetchingRejected, refetch: refetchRejected } = useGetRejectedPublicLevelsQuery({ page: rejectedPage, limit: 10 });

  const [approveLevel, { isLoading: isApproving }] = useApprovePendingLevelMutation();
  const [rejectLevel, { isLoading: isRejecting }] = useRejectPendingLevelMutation();

  // --- Modals State ---
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [pubDetailsModalOpen, setPubDetailsModalOpen] = useState(false);
  const [pendingPreviewModalOpen, setPendingPreviewModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // Handler for Published Levels
  const handleOpenPubDetails = (level) => {
    setSelectedLevel(level);
    setPubDetailsModalOpen(true);
  };

  const handleConfirmUnpublish = (levelId) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-slate-900 border border-slate-700 shadow-2xl rounded-2xl p-4 pointer-events-auto text-white`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-rose-500/20 text-rose-500 flex items-center justify-center font-bold text-lg">
              🚫
            </div>
            <div>
              <p className="text-sm font-bold text-white">Unpublish Public Level?</p>
              <p className="text-xs text-slate-400">
                Remove <strong className="text-cyan-400">{selectedLevel?.levelCode || selectedLevel?.title}</strong> from Public Arcade?
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg"
              onClick={() => toast.remove(t.id)}
            >
              Cancel
            </button>
            <button
              className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg shadow-lg"
              onClick={() => {
                toast.remove(t.id);
                toast.success("✅ Level unpublished successfully!");
                setPubDetailsModalOpen(false);
                refetchPub();
              }}
            >
              Confirm Unpublish
            </button>
          </div>
        </div>
      ),
      { duration: 5000 }
    );
  };

  // Handlers for Pending Approvals
  const handleOpenPendingPreview = (level) => {
    setSelectedLevel(level);
    setPendingPreviewModalOpen(true);
  };

  const handleApprovePending = async (levelId) => {
    try {
      const res = await approveLevel(levelId).unwrap();
      toast.success(res?.message || "✅ Level approved and published to Public Arcade!");
      setPendingPreviewModalOpen(false);
      setPlayModalOpen(false);
      refetchPending();
      refetchPub();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to approve level");
    }
  };

  const handleOpenRejectModal = (level) => {
    setSelectedLevel(level);
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please enter a valid rejection reason");
      return;
    }

    try {
      const res = await rejectLevel({ levelId: selectedLevel._id, reason: rejectionReason.trim() }).unwrap();
      toast.success(res?.message || "🔴 Level rejected successfully!");
      setRejectModalOpen(false);
      setPendingPreviewModalOpen(false);
      setPlayModalOpen(false);
      refetchPending();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to reject level");
    }
  };

  // --- Lists & Tables with Fallback Slicing ---
  const rawPublicList = pubData?.data?.levels || pubData?.response?.data?.levels || pubData?.data || [];
  const pubTotalPages = pubData?.data?.totalPages || pubData?.response?.data?.totalPages || Math.ceil(rawPublicList.length / 10) || 1;
  const publicList = rawPublicList.length > 10 ? rawPublicList.slice((pubPage - 1) * 10, pubPage * 10) : rawPublicList;

  const rawPendingList = pendingData?.data?.levels || pendingData?.response?.data?.levels || pendingData?.data || [];
  const pendingTotalPages = pendingData?.data?.totalPages || pendingData?.response?.data?.totalPages || Math.ceil(rawPendingList.length / 10) || 1;
  const pendingList = rawPendingList.length > 10 ? rawPendingList.slice((pendingPage - 1) * 10, pendingPage * 10) : rawPendingList;

  // 1. Published Table Rows
  const pubColumns = [
    { name: "CODE", align: "left" },
    { name: "TITLE", align: "left" },
    { name: "CREATOR", align: "left" },
    { name: "DIFFICULTY", align: "center" },
    { name: "NODES & MOVES", align: "center" },
    { name: "LIKES & PLAYS", align: "center" },
    { name: "STATUS", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const pubRows = publicList.map((item) => {
    const creatorUser = item.createdBy || item.creator || {};
    const creatorName = typeof creatorUser === "string" ? creatorUser : (creatorUser.name || creatorUser.username || "Creator");
    const creatorAvatar = creatorUser.picture || creatorUser.avatar;

    return {
      CODE: (
        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: "#38bdf8 !important" }}>
          {item.levelCode || "#CS-0000"}
        </VuiTypography>
      ),
      TITLE: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {item.title || "Custom Circuit"}
        </VuiTypography>
      ),
      CREATOR: (
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <Avatar src={creatorAvatar} alt={creatorName} sx={{ width: 28, height: 28 }} />
          <VuiTypography variant="caption" color="text" fontWeight="medium">
            {creatorName}
          </VuiTypography>
        </VuiBox>
      ),
    DIFFICULTY: (
      <Chip
        label={item.difficulty || "Medium"}
        color={item.difficulty === "Hard" || item.difficulty === "Expert" ? "error" : "warning"}
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    ),
    "NODES & MOVES": (
      <VuiTypography variant="caption" color="white" fontWeight="bold">
        {item.nodes?.length || item.nodesCount || 0} Nodes | {item.bestMoves || 0} Moves
      </VuiTypography>
    ),
    "LIKES & PLAYS": (
      <VuiTypography variant="caption" color="white" fontWeight="bold">
        ❤️ {item.likesCount !== undefined ? item.likesCount : (item.likes?.length || 0)} | 🎮 {(item.playsCount || 0).toLocaleString()}
      </VuiTypography>
    ),
    STATUS: (
      <Chip label="PUBLIC ARCADE" color="success" size="small" sx={{ fontWeight: "bold" }} />
    ),
    ACTIONS: (
      <Button
        variant="contained"
        size="small"
        startIcon={<Visibility />}
        onClick={() => handleOpenPubDetails(item)}
        sx={{
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: "#06b6d4 !important",
          color: "#ffffff !important",
          borderRadius: "8px",
          px: 2.5,
          boxShadow: "0 0 12px rgba(6, 182, 212, 0.4)",
          "&:hover": {
            backgroundColor: "#0891b2 !important",
            boxShadow: "0 0 18px rgba(6, 182, 212, 0.7)",
          },
        }}
      >
        View Details
      </Button>
    ),
  };
});

  // 2. Pending Table Rows
  const pendingColumns = [
    { name: "CODE", align: "left" },
    { name: "TITLE", align: "left" },
    { name: "CREATOR", align: "left" },
    { name: "DIFFICULTY", align: "center" },
    { name: "NODES", align: "center" },
    { name: "SUBMITTED AT", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const pendingRows = pendingList.map((item) => {
    const creatorUser = item.createdBy || item.creator || {};
    const creatorName = typeof creatorUser === "string" ? creatorUser : (creatorUser.name || creatorUser.username || "Creator");
    const creatorAvatar = creatorUser.picture || creatorUser.avatar;

    return {
      CODE: (
        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: "#38bdf8 !important" }}>
          {item.levelCode || "#CS-0000"}
        </VuiTypography>
      ),
      TITLE: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {item.title || "Custom Circuit"}
        </VuiTypography>
      ),
      CREATOR: (
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <Avatar src={creatorAvatar} alt={creatorName} sx={{ width: 28, height: 28 }} />
          <VuiTypography variant="caption" color="text" fontWeight="medium">
            {creatorName}
          </VuiTypography>
        </VuiBox>
      ),
    DIFFICULTY: (
      <Chip
        label={item.difficulty || "Medium"}
        color={item.difficulty === "Hard" || item.difficulty === "Expert" ? "error" : "warning"}
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    ),
    NODES: (
      <VuiTypography variant="caption" color="white" fontWeight="bold">
        {item.nodes?.length || item.nodesCount || 0} Nodes
      </VuiTypography>
    ),
    "SUBMITTED AT": (
      <VuiTypography variant="caption" color="text">
        {item.createdAt}
      </VuiTypography>
    ),
    ACTIONS: (
      <Button
        variant="contained"
        size="small"
        startIcon={<Visibility />}
        onClick={() => handleOpenPendingPreview(item)}
        sx={{
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: "#06b6d4 !important",
          color: "#ffffff !important",
          borderRadius: "8px",
          px: 2.5,
          boxShadow: "0 0 12px rgba(6, 182, 212, 0.4)",
          "&:hover": {
            backgroundColor: "#0891b2 !important",
            boxShadow: "0 0 18px rgba(6, 182, 212, 0.7)",
          },
        }}
      >
        Preview
      </Button>
    ),
  };
});

  // 3. Rejected Table Rows
  const rawRejectedList = rejectedData?.data?.levels || rejectedData?.response?.data?.levels || rejectedData?.data || [];
  const rejectedTotalPages = rejectedData?.data?.totalPages || rejectedData?.response?.data?.totalPages || Math.ceil(rawRejectedList.length / 10) || 1;
  const rejectedList = rawRejectedList.length > 10 ? rawRejectedList.slice((rejectedPage - 1) * 10, rejectedPage * 10) : rawRejectedList;

  const rejectedColumns = [
    { name: "CODE", align: "left" },
    { name: "TITLE", align: "left" },
    { name: "CREATOR", align: "left" },
    { name: "DIFFICULTY", align: "center" },
    { name: "REJECTION REASON", align: "left" },
    { name: "REJECTED AT", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const rejectedRows = rejectedList.map((item) => {
    const creatorUser = item.createdBy || item.creator || {};
    const creatorName = typeof creatorUser === "string" ? creatorUser : (creatorUser.name || creatorUser.username || "Creator");
    const creatorAvatar = creatorUser.picture || creatorUser.avatar;

    return {
      CODE: (
        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: "#38bdf8 !important" }}>
          {item.levelCode || "#CS-0000"}
        </VuiTypography>
      ),
      TITLE: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {item.title || "Custom Circuit"}
        </VuiTypography>
      ),
      CREATOR: (
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <Avatar src={creatorAvatar} alt={creatorName} sx={{ width: 28, height: 28 }} />
          <VuiTypography variant="caption" color="text" fontWeight="medium">
            {creatorName}
          </VuiTypography>
        </VuiBox>
      ),
      DIFFICULTY: (
        <Chip
          label={item.difficulty || "Medium"}
          color={item.difficulty === "Hard" || item.difficulty === "Expert" ? "error" : "warning"}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
      "REJECTION REASON": (
        <VuiTypography variant="caption" color="error" fontWeight="bold">
          🚫 {item.rejectionReason || "Did not meet submission guidelines"}
        </VuiTypography>
      ),
      "REJECTED AT": (
        <VuiTypography variant="caption" color="text">
          {item.rejectedAt ? new Date(item.rejectedAt).toLocaleDateString() : item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "Recently"}
        </VuiTypography>
      ),
      ACTIONS: (
        <Button
          variant="contained"
          size="small"
          startIcon={<Visibility />}
          onClick={() => handleOpenPendingPreview(item)}
          sx={{
            fontSize: "11px",
            fontWeight: "bold",
            backgroundColor: "#f43f5e !important",
            color: "#ffffff !important",
            borderRadius: "8px",
            px: 2,
            boxShadow: "0 0 12px rgba(244, 63, 94, 0.4)",
            "&:hover": {
              backgroundColor: "#e11d48 !important",
            },
          }}
        >
          Inspect
        </Button>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Custom Glowing Cyber Tab Switcher */}
        <VuiBox
          display="flex"
          gap={1.5}
          mb={3}
          p={0.8}
          sx={{
            backgroundColor: "#1e293b",
            borderRadius: "16px",
            border: "1px solid #334155",
            width: "fit-content",
          }}
        >
          <Button
            onClick={() => setTabValue(0)}
            startIcon={<Public />}
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: "12px",
              fontWeight: "bold",
              fontSize: "13px",
              backgroundColor: tabValue === 0 ? "#06b6d4 !important" : "transparent",
              color: tabValue === 0 ? "#ffffff !important" : "#94a3b8 !important",
              boxShadow: tabValue === 0 ? "0 0 14px rgba(6, 182, 212, 0.4)" : "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                color: "#ffffff !important",
                backgroundColor: tabValue === 0 ? "#0891b2 !important" : "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            Published Arcade Levels
          </Button>

          <Button
            onClick={() => setTabValue(1)}
            startIcon={<HourglassEmpty />}
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: "12px",
              fontWeight: "bold",
              fontSize: "13px",
              backgroundColor: tabValue === 1 ? "#06b6d4 !important" : "transparent",
              color: tabValue === 1 ? "#ffffff !important" : "#94a3b8 !important",
              boxShadow: tabValue === 1 ? "0 0 14px rgba(6, 182, 212, 0.4)" : "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                color: "#ffffff !important",
                backgroundColor: tabValue === 1 ? "#0891b2 !important" : "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            Pending Approvals ({pendingList.length})
          </Button>

          <Button
            onClick={() => setTabValue(2)}
            startIcon={<Cancel />}
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: "12px",
              fontWeight: "bold",
              fontSize: "13px",
              backgroundColor: tabValue === 2 ? "#f43f5e !important" : "transparent",
              color: tabValue === 2 ? "#ffffff !important" : "#94a3b8 !important",
              boxShadow: tabValue === 2 ? "0 0 14px rgba(244, 63, 94, 0.4)" : "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                color: "#ffffff !important",
                backgroundColor: tabValue === 2 ? "#e11d48 !important" : "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            Rejected Submissions ({rejectedList.length})
          </Button>
        </VuiBox>

        {/* Tab 0: Published Levels */}
        {tabValue === 0 && (
          <>
            <Card>
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Published Public Community Levels
                </VuiTypography>
              </VuiBox>
              <VuiBox p={2}>
                {isLoadingPub || isFetchingPub ? (
                  <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                    <CircularProgress color="info" size={40} />
                    <VuiTypography variant="button" color="text" fontWeight="bold">
                      Loading Published Arcade Levels...
                    </VuiTypography>
                  </VuiBox>
                ) : pubRows.length === 0 ? (
                  <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <VuiTypography variant="h3" mb={1}>📭</VuiTypography>
                    <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                      No Published Public Levels Found
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      Approved community levels will appear here once published.
                    </VuiTypography>
                  </VuiBox>
                ) : (
                  <Table columns={pubColumns} rows={pubRows} />
                )}
              </VuiBox>
            </Card>

            <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Pagination
                count={pubTotalPages}
                page={pubPage}
                onChange={(e, val) => setPubPage(val)}
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
                  "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#06b6d4 !important", color: "#ffffff !important" },
                }}
              />
            </Card>
          </>
        )}

        {/* Tab 1: Pending Approvals */}
        {tabValue === 1 && (
          <>
            <Card>
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Pending Community Submissions Awaiting Approval
                </VuiTypography>
              </VuiBox>
              <VuiBox p={2}>
                {isLoadingPending || isFetchingPending ? (
                  <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                    <CircularProgress color="info" size={40} />
                    <VuiTypography variant="button" color="text" fontWeight="bold">
                      Loading Pending Submissions...
                    </VuiTypography>
                  </VuiBox>
                ) : pendingRows.length === 0 ? (
                  <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <VuiTypography variant="h3" mb={1}>🎉</VuiTypography>
                    <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                      All Submissions Up to Date!
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      There are no pending community levels waiting for approval.
                    </VuiTypography>
                  </VuiBox>
                ) : (
                  <Table columns={pendingColumns} rows={pendingRows} />
                )}
              </VuiBox>
            </Card>

            <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Pagination
                count={pendingTotalPages}
                page={pendingPage}
                onChange={(e, val) => setPendingPage(val)}
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
                  "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#06b6d4 !important", color: "#ffffff !important" },
                }}
              />
            </Card>
          </>
        )}

        {/* Tab 2: Rejected Submissions */}
        {tabValue === 2 && (
          <>
            <Card>
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Rejected Community Submissions
                </VuiTypography>
              </VuiBox>
              <VuiBox p={2}>
                {isLoadingRejected || isFetchingRejected ? (
                  <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                    <CircularProgress color="error" size={40} />
                    <VuiTypography variant="button" color="text" fontWeight="bold">
                      Loading Rejected Submissions...
                    </VuiTypography>
                  </VuiBox>
                ) : rejectedRows.length === 0 ? (
                  <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <VuiTypography variant="h3" mb={1}>🛡️</VuiTypography>
                    <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                      No Rejected Submissions Found
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      Submissions rejected during moderation will be archived here with feedback reasons.
                    </VuiTypography>
                  </VuiBox>
                ) : (
                  <Table columns={rejectedColumns} rows={rejectedRows} />
                )}
              </VuiBox>
            </Card>

            <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Pagination
                count={rejectedTotalPages}
                page={rejectedPage}
                onChange={(e, val) => setRejectedPage(val)}
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
                  "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#f43f5e !important", color: "#ffffff !important" },
                }}
              />
            </Card>
          </>
        )}
      </VuiBox>

      {/* 1. Published Level View Details Modal Dialog */}
      <Dialog
        open={pubDetailsModalOpen}
        onClose={() => setPubDetailsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { backgroundColor: "#0f172a", color: "#fff", borderRadius: "20px", border: "1px solid #06b6d4" },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e293b", pb: 2 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <Chip label={selectedLevel?.levelCode} color="primary" sx={{ fontWeight: "bold" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold">
                {selectedLevel?.title}
              </VuiTypography>
            </VuiBox>
            <Chip label={selectedLevel?.difficulty} color="warning" sx={{ fontWeight: "bold" }} />
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2} mb={3} sx={{ backgroundColor: "#1e293b", borderRadius: "12px", border: "1px solid #334155" }}>
            <VuiBox display="flex" alignItems="center" gap={2}>
              <Avatar
                src={(selectedLevel?.createdBy?.picture || selectedLevel?.createdBy?.avatar) || (selectedLevel?.creator?.avatar || selectedLevel?.creator?.picture)}
              />
              <VuiBox display="flex" flexDirection="column">
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  {selectedLevel?.createdBy?.name || selectedLevel?.creator?.name || selectedLevel?.creator || "Creator"}
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  {selectedLevel?.createdBy?.email || selectedLevel?.creator?.email || "No email"}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
            <VuiTypography variant="caption" color="text">
              Published: {selectedLevel?.createdAt}
            </VuiTypography>
          </VuiBox>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "120px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Favorite sx={{ color: "#f43f5e", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">COMMUNITY LIKES</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  ❤️ {selectedLevel?.likesCount !== undefined ? selectedLevel.likesCount : (selectedLevel?.likes?.length || 0)}
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "120px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <SportsEsports sx={{ color: "#38bdf8", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">TOTAL PLAYS</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  🎮 {(selectedLevel?.playsCount || 0).toLocaleString()}
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "120px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Memory sx={{ color: "#06b6d4", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">CIRCUIT NODES</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  {selectedLevel?.nodes?.length || selectedLevel?.nodesCount || 0} Nodes
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "120px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Speed sx={{ color: "#f59e0b", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">TARGET MOVES</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  {selectedLevel?.bestMoves || 0} Moves
                </VuiTypography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button
            onClick={() => {
              setPubDetailsModalOpen(false);
              setPlayModalOpen(true);
            }}
            variant="contained"
            startIcon={<PlayArrow />}
            sx={{ fontSize: "12px", fontWeight: "bold", backgroundColor: "#10b981 !important", color: "#fff !important", borderRadius: "10px", px: 3 }}
          >
            🎮 Play & Test Circuit Diagram
          </Button>

          <Button
            onClick={() => handleConfirmUnpublish(selectedLevel._id)}
            variant="contained"
            startIcon={<Delete />}
            sx={{ fontSize: "12px", fontWeight: "bold", backgroundColor: "#f43f5e !important", color: "#fff !important", borderRadius: "10px", px: 3 }}
          >
            Unpublish Level
          </Button>

          <Button onClick={() => setPubDetailsModalOpen(false)} sx={{ color: "#94a3b8" }}>
            Close Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. Pending Level Preview Modal Dialog */}
      <Dialog
        open={pendingPreviewModalOpen}
        onClose={() => setPendingPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { backgroundColor: "#0f172a", color: "#fff", borderRadius: "20px", border: "1px solid #06b6d4" },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e293b", pb: 2 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <Chip label={selectedLevel?.levelCode} color="primary" sx={{ fontWeight: "bold" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold">
                {selectedLevel?.title}
              </VuiTypography>
            </VuiBox>
            <Chip label={selectedLevel?.difficulty} color="warning" sx={{ fontWeight: "bold" }} />
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2} mb={3} sx={{ backgroundColor: "#1e293b", borderRadius: "12px", border: "1px solid #334155" }}>
            <VuiBox display="flex" alignItems="center" gap={2}>
              <Avatar
                src={(selectedLevel?.createdBy?.picture || selectedLevel?.createdBy?.avatar) || (selectedLevel?.creator?.avatar || selectedLevel?.creator?.picture)}
              />
              <VuiBox display="flex" flexDirection="column">
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  {selectedLevel?.createdBy?.name || selectedLevel?.creator?.name || selectedLevel?.creator || "Creator"}
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  {selectedLevel?.createdBy?.email || selectedLevel?.creator?.email || "No email"}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
            <VuiTypography variant="caption" color="text">
              Submitted: {selectedLevel?.createdAt}
            </VuiTypography>
          </VuiBox>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "130px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Memory sx={{ color: "#06b6d4", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">TOTAL NODES</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  {selectedLevel?.nodesCount || 7} Nodes
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "130px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Extension sx={{ color: "#38bdf8", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text" mb={0.5}>GATES INCLUDED</VuiTypography>
                <VuiBox display="flex" flexWrap="wrap" justifyContent="center" gap={0.8}>
                  {(selectedLevel?.gateTypes || ["XOR", "AND", "NOT"]).map((gate, i) => (
                    <Chip key={i} label={gate} size="small" sx={{ backgroundColor: "#06b6d4 !important", color: "#fff !important", fontWeight: "bold" }} />
                  ))}
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "130px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Speed sx={{ color: "#f59e0b", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">BEST MOVES TARGET</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  {selectedLevel?.bestMoves || 4} Moves
                </VuiTypography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button
            onClick={() => {
              setPendingPreviewModalOpen(false);
              setPlayModalOpen(true);
            }}
            variant="contained"
            sx={{ fontSize: "12px", fontWeight: "bold", backgroundColor: "#10b981 !important", color: "#fff !important", borderRadius: "10px", px: 3 }}
          >
            🎮 Test & Play Circuit Diagram
          </Button>

          <Button
            onClick={() => handleOpenRejectModal(selectedLevel)}
            variant="contained"
            sx={{ fontSize: "12px", fontWeight: "bold", backgroundColor: "#f43f5e !important", color: "#fff !important", borderRadius: "10px", px: 3 }}
          >
            Reject Level
          </Button>

          <Button
            onClick={() => handleApprovePending(selectedLevel._id)}
            variant="contained"
            disabled={isApproving}
            sx={{ fontSize: "12px", fontWeight: "bold", backgroundColor: "#06b6d4 !important", color: "#fff !important", borderRadius: "10px", px: 3, boxShadow: "0 0 14px rgba(6, 182, 212, 0.4)" }}
          >
            Approve & Publish Level
          </Button>

          <Button onClick={() => setPendingPreviewModalOpen(false)} sx={{ color: "#94a3b8" }}>
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mandatory Rejection Reason Dialog */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { backgroundColor: "#0f172a", color: "#fff", borderRadius: "20px", border: "1px solid #f43f5e" },
        }}
      >
        <DialogTitle sx={{ color: "#f43f5e", fontWeight: "bold", borderBottom: "1px solid #1e293b" }}>
          🛑 Reject Level Submission ({selectedLevel?.levelCode})
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <VuiTypography variant="caption" color="text" mb={2} display="block">
            Please state the mandatory rejection reason to notify creator <strong>{selectedLevel?.creator?.name || selectedLevel?.creator}</strong>:
          </VuiTypography>

          <Box>
            <label style={{ color: "#fb7185", fontSize: "12px", fontWeight: "bold", display: "block", marginBottom: "6px" }}>
              Rejection Reason (Required)
            </label>
            <textarea
              rows={3}
              placeholder="e.g. Unsolvable circuit logic deadlock / overlapping logic gates"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              style={{
                width: "100%",
                backgroundColor: "#1e293b",
                border: "1px solid #334155",
                borderRadius: "10px",
                color: "#ffffff",
                padding: "12px 14px",
                fontSize: "14px",
                outline: "none",
                resize: "vertical",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button onClick={() => setRejectModalOpen(false)} sx={{ color: "#94a3b8" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            disabled={isRejecting || !rejectionReason.trim()}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#f43f5e !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(244, 63, 94, 0.4)",
            }}
          >
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🎮 Interactive ReactFlow Circuit Canvas Player */}
      {playModalOpen && (
        <RunAndTest
          data={
            selectedLevel?.nodes && selectedLevel?.edges
              ? { levelData: { nodes: selectedLevel.nodes, edges: selectedLevel.edges }, outputId: selectedLevel.completeNodeId || "4" }
              : {
                  levelData: {
                    nodes: [
                      { id: "1", type: "input", data: { label: "Switch 1" }, position: { x: 200, y: 450 } },
                      { id: "2", type: "input", data: { label: "Switch 2" }, position: { x: 400, y: 450 } },
                      { id: "3", type: "gate", data: { label: "AND", inputs: ["1", "2"], outputs: ["4"] }, position: { x: 300, y: 250 } },
                      { id: "4", type: "output", data: { label: "LEVEL COMPLETE", inputs: ["3"] }, position: { x: 300, y: 80 } },
                    ],
                    edges: [
                      { id: "e1-3", source: "1", sourceHandle: "output", target: "3", targetHandle: "input-1", animated: true },
                      { id: "e2-3", source: "2", sourceHandle: "output", target: "3", targetHandle: "input-2", animated: true },
                      { id: "e3-4", source: "3", sourceHandle: "output", target: "4", targetHandle: "input-3", animated: true },
                    ],
                  },
                  outputId: "4",
                }
          }
          onClose={() => setPlayModalOpen(false)}
          onApprove={tabValue === 1 ? () => handleApprovePending(selectedLevel._id) : undefined}
          onReject={tabValue === 1 ? () => handleOpenRejectModal(selectedLevel) : undefined}
        />
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default PublicLevelsManagement;
